import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Alert,
  TreeSelect,
  Popover,
  DatePicker,
  Button,
} from 'antd'
import { useFlags } from 'launchdarkly-react-client-sdk'
import isEmpty from 'lodash/isEmpty'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Row, Col } from 'reactstrap'

import {
  checkPermission,
} from '@app/common/utils/helpers'
import Table from '@app/common/tables/Table'
import Pagination from '@app/common/widgets/Pagination'
import {
  DATE_RANGE_FILTER_TEXT,
  KEY_3_MONTHS,
  KEY_6_MONTHS,
  KEY_12_MONTHS,
  KEY_ALL_TIME,
  KEY_CUSTOM_RANGE,
} from '@app/common/constants/dateFilter'
import Rephrase from '@app/common/utils/Rephrase'
import OrderSearch from './OrderSearch'
import OrderTableColumns from './OrderTableColumns'
import DefaultFilters from './OrderTableFilters/DefaultFilters'
import SingleFilter from './OrderTableFilters/SingleFilter'
import { TrackingContext } from '@app/pages/orders/TrackingContext'
import OrderGetNext from './OrderGetNext'
import { fetchReviewerQueues } from '@app/store/reducers/user/reviewerQueuesSlice'
import { selectCurrentUserId, selectReviewerQueues, selectUserFilterSettings } from '@app/store/reducers/user/userSelectors'
import { updateOrdersUnderwriter, updateOrdersUnderwriterState } from '@app/store/reducers/underwriters/underwritersSlice'
import { selectOrdersUnderwriterState } from '@app/store/reducers/underwriters/undewritersSelectors'


const { RangePicker } = DatePicker

export default function Orders(props) {
  const dispatch = useDispatch()

  const currentUserId = useSelector(selectCurrentUserId)
  const reviewerQueues = useSelector(selectReviewerQueues)
  const filterSettings = useSelector(selectUserFilterSettings)
  const ordersUnderwriterState = useSelector(selectOrdersUnderwriterState)
  const {
    closingDateDaysToCloseColumn,
    enableVaOrderDueAndInspectionDate,
    ordersPageSize,
  } = useFlags() || {}

  const [selectedOrderIds, setSelectedOrderIds] = useState([])
  const [trackState, setTrackState] = useState({
    searched: false,
    searchesUntilOrderClick: 0,
    pageClicksUntilOrderClick: 0.0,
  })
  useEffect(() => {
    return () => {
      setTrackState({ searched: false, searchesUntilOrderClick: 0, pageClicksUntilOrderClick: 0 })
    }
  }, [])

  useEffect(() => {
    dispatch(fetchReviewerQueues(currentUserId))
  }, [dispatch, currentUserId])

  const orderList = useMemo(() => {
    const result = []
    for (const order of props.orders.data) {
      result.push(order)
      if (order.follow_up_orders) {
        const totalOrders = order.follow_up_orders.length
        let count = 0
        for (const follow_up of order.follow_up_orders) {
          if (count === 0) {
            result.push({
              ...follow_up,
              primaryOrderId: order.id,
              is_first: true,
              is_last: order.follow_up_orders.length === 1,
            })
          } else if (count === (totalOrders - 1)) {
            result.push({ ...follow_up, primaryOrderId: order.id, is_last: true })
          } else {
            result.push({ ...follow_up, primaryOrderId: order.id })
          }
          count++
        }
      }
    }
    return result
  }, [
    props.orders.data,
  ])

  const content = (
    <div>
      <Button
        type='text'
        onClick={ props.handleDateFilterChange }
        daterange={ KEY_3_MONTHS }
      >
        { DATE_RANGE_FILTER_TEXT[KEY_3_MONTHS] }
      </Button>
      <br />
      <Button
        type='text'
        daterange={KEY_6_MONTHS}
        onClick={props.handleDateFilterChange}
      >
        {DATE_RANGE_FILTER_TEXT[KEY_6_MONTHS]}
      </Button>
      <br />
      <Button
        type='text'
        daterange={KEY_12_MONTHS}
        onClick={props.handleDateFilterChange}
      >
        {DATE_RANGE_FILTER_TEXT[KEY_12_MONTHS]}
      </Button>
      <br />
      <Button
        type='text'
        daterange={KEY_ALL_TIME}
        onClick={props.handleDateFilterChange}
      >
        {DATE_RANGE_FILTER_TEXT[KEY_ALL_TIME]}
      </Button>
      <br />
      <Button
        type='text'
        daterange={ KEY_CUSTOM_RANGE }
        onClick={props.handleDateFilterChange}
      >
        {DATE_RANGE_FILTER_TEXT[KEY_CUSTOM_RANGE]}
      </Button>
    </div>
  )

  const unapprovedMsgFilterSetting = filterSettings?.unapproved_messages

  const loanOfficers = useMemo(() => {
    let sortedLoanOfficers = []
    if (!isEmpty(props.officer_filter)) {
      sortedLoanOfficers = props.officer_filter.sort((a, b) => (a.name > b.name) ? 1 : -1)
    }

    return sortedLoanOfficers.map(a => {
      const formattedLoanOfficer = {}
      formattedLoanOfficer.title = a.name
      formattedLoanOfficer.search = a.name
      formattedLoanOfficer.value = a.id
      return formattedLoanOfficer
    })
  }, [
    props.officer_filter,
  ])

  const underwriters = useMemo(() => {
    let sortedUnderwriters = []
    if (!isEmpty(props.underwriter_filter)) {
      sortedUnderwriters = props.underwriter_filter.sort((a, b) => (a.name > b.name) ? 1 : -1)
    }

    return sortedUnderwriters.map(a => {
      const formattedUnderwriter = {}
      formattedUnderwriter.title = a.name
      formattedUnderwriter.search = a.name
      formattedUnderwriter.value = a.id
      return formattedUnderwriter
    })
  }, [
    props.underwriter_filter,
  ])

  const assignedUsers = useMemo(() => {
    let sortedAssignedUsers = []
    if (!isEmpty(props.assigned_filter)) {
      sortedAssignedUsers = props.assigned_filter.sort((a, b) => (a.name > b.name) ? 1 : -1)
    }

    const result = sortedAssignedUsers.map(({ id, name }) => ({
      title: name,
      value: id,
      search: name,
    }))
    return result
  }, [
    props.assigned_filter,
  ])

  const renderCombinedAppraiserFilter = () => {
    const panelistTreeData = []
    const companyMap = {}
    const companyAppraiserMapping = {}
    const resourceIdMap = {}


    const reggoraNetworkSelected = props.selectedAppraisers.filter((a) => a.value === 'amc').length > 0
    // show the reggora network if the user searches for it
    if (!reggoraNetworkSelected && props.has_amc_orders && 'reggora network'.includes(props.panelistSearchText.toLowerCase()) && !props.panelistSearch.isLoading) {
      const reggoraNetwork = {}
      reggoraNetwork.title = 'Reggora Network'
      reggoraNetwork.value = 'amc'
      reggoraNetwork.search = 'Reggora Network'
      panelistTreeData.push(reggoraNetwork)
      // adding in reggora network to resourceIdMap so we don't duplicate object in filter
      resourceIdMap[reggoraNetwork.value] = 1
    }

    if (props.panelistSearch && !props.panelistSearch.isLoading &&
      props.panelistSearch.data && props.panelistSearch.data.results) {
      props.panelistSearch.data.results.forEach(panelist => {
        const panelistDataObject = {
          title: panelist.panelist_name,
          value: panelist.resource_id,
          // used by tree select component to search. We want to display all results if the firm or panelist name matches
          search: panelist.panelist_name + ' ' + panelist.firm_name,
        }

        // add company panelist to tree data
        if (panelist.panelist_type === 'company') {
          if (!(panelist.resource_id in companyMap)) {
            panelistTreeData.push(panelistDataObject)
            companyMap[panelist.resource_id] = 1
            resourceIdMap[panelist.resource_id] = 1
          }
        // add appraiser data to company appraiser map
        } else if (panelist.panelist_type === 'appraiser') {
          if (panelist.company in companyAppraiserMapping) {
            companyAppraiserMapping[panelist.company].push(panelistDataObject)
            resourceIdMap[panelist.resource_id] = 1
          } else {
            companyAppraiserMapping[panelist.company] = [panelistDataObject]
            resourceIdMap[panelist.resource_id] = 1
          }
          // add appraiser's company to tree data if it doesn't already exist
          if (!(panelist.company in companyMap)) {
            panelistTreeData.push({
              title: panelist.firm_name,
              value: panelist.company,
              search: panelist.panelist_name + ' ' + panelist.firm_name,
            })
            companyMap[panelist.company] = 1
            resourceIdMap[panelist.company] = 1
          }
        }
      })
    }

    // iterate through company tree data and add children appraisers using our company appraiser map
    panelistTreeData.forEach(panelistTreeDataObject => {
      const companyId = panelistTreeDataObject.value
      if (companyId in companyAppraiserMapping) {
        panelistTreeDataObject.children = companyAppraiserMapping[companyId].sort(
          (a, b) => (a.title.toLowerCase() > b.title.toLowerCase()) ? 1 : -1)
      }
    })

    props.selectedAppraisers.forEach((selectedAppraiser) => {
      if (!(selectedAppraiser.value in resourceIdMap)) {
        panelistTreeData.push({
          title: selectedAppraiser.label,
          value: selectedAppraiser.value,
          // set search to panelistSearchText so that selected appraisers are displayed
          search: props.panelistSearchText,
        })
      }
    })

    return (
      <Col className='appraiser-col'>
        <TreeSelect
          disabled={props.filters.isLoading}
          showSearch
          treeCheckable={!props.panelistSearch.isLoading && !props.filters.isLoading}
          value={props.selectedAppraisers}
          placeholder={<Rephrase>Filter by Vendor</Rephrase>}
          allowClear
          labelInValue={true}
          multiple
          treeDefaultExpandAll
          treeNodeFilterProp='search'
          treeNodeLabelProp='title'
          treeData={panelistTreeData}
          searchValue={props.panelistSearchText}
          onSearch={props.handlePanelistSearch}
          onChange={props.handlePanelistChange}
          className='tree-filter'
          dropdownClassName='tree-filter-dropdown'
        />
      </Col>
    )
  }

  const availablePipelineViews = useMemo(() => {
    // calculate list of pipeline views user can access
    const customPipelineFilters = props.pipelineViews?.data || []
    const reviewerQueueFilters = props.reviewerQueueQueryBuilders?.data || []
    const allPipelineFilters = [
      ...customPipelineFilters,
      ...reviewerQueueFilters,
    ]
    return allPipelineFilters.filter(
      (view) => view.user_roles.includes(props.current_user_role_id)
    )
  }, [
    props.current_user_role_id,
    props.pipelineViews,
    props.reviewerQueueQueryBuilders,
  ])

  const canClear = props.selectedDefaultViews.length > 0 || props.selectedPipelineViews.length > 0 ||
    props.selectedAssignedUsers.length > 0 || props.selectedLoanOfficers.length > 0 ||
    props.selectedAppraisers.length > 0

  // Table requires children to be list of Column components, so we call OrderTableColumns directly
  const {
    cu_filter,
    cu_max,
    cu_min,
    cuHandler,
    custom_los_fields,
    custom_panel,
    is_amc_lender,
    lenderFilterOptions,
    onFilter,
    paymentStatusFilterOptions,
    pipeline_column_order,
    pipeline_column_settings,
    productFilterOptions,
    selectedDefaultViews,
    status_filter,
    updateFlagCount,
  } = props
  const cols = useMemo(() => {
    const handleUpdateUnderwriter = (value, orderIds) => {
      dispatch(updateOrdersUnderwriter({
        underwriterId: value,
        orderIds,
      }))
      dispatch(updateOrdersUnderwriterState({
        underwriterId: value,
        orderIds,
      }))
    }
    return OrderTableColumns({
      closingDateDaysToCloseColumn,
      cu_filter,
      cu_max,
      cu_min,
      cuHandler,
      custom_los_fields,
      custom_panel,
      enableVaOrderDueAndInspectionDate,
      handleUpdateUnderwriter,
      is_amc_lender,
      lenderFilterOptions,
      onFilter,
      orderList,
      ordersUnderwriterState,
      paymentStatusFilterOptions,
      pipeline_column_order,
      pipeline_column_settings,
      productFilterOptions,
      selectedDefaultViews,
      selectedOrderIds,
      setSelectedOrderIds,
      status_filter,
      trackState,
      unapprovedMsgFilterSetting,
      underwriters,
      updateFlagCount,
    })
  }, [
    closingDateDaysToCloseColumn,
    cu_filter,
    cu_max,
    cu_min,
    cuHandler,
    custom_los_fields,
    custom_panel,
    dispatch,
    enableVaOrderDueAndInspectionDate,
    is_amc_lender,
    lenderFilterOptions,
    onFilter,
    orderList,
    ordersUnderwriterState,
    paymentStatusFilterOptions,
    pipeline_column_order,
    pipeline_column_settings,
    productFilterOptions,
    selectedDefaultViews,
    selectedOrderIds,
    setSelectedOrderIds,
    status_filter,
    trackState,
    unapprovedMsgFilterSetting,
    underwriters,
    updateFlagCount,
  ])

  return (
    <TrackingContext.Provider value={{ trackState: trackState, setTrackState: setTrackState }}>
      <Container fluid className="pl-0">
        <Row>
          <Col xs="12" sm="4" md="4" lg="3" xl="2" className="pl-4 pt-3 pb-5 pr-1" style={{ backgroundColor: '#f4f5f7', border: '1px solid #e7e8e8', height: '100vh', overflow: 'scroll' }}>
            <div className='top-filters'>
              <div className='filters-clear'>
                <h5 className='ml-1 mb-0'>Filters</h5>
                {canClear && <div className="clear-filters ml-1 mb-2" onClick={props.clearFilters}>Clear</div>}
              </div>
              {/* Loan Officer Selection Filter */}
              {pipeline_column_settings && pipeline_column_settings.loan_officer && (
                <Col className="loan-officer-col">
                  <TreeSelect
                    value={props.selectedLoanOfficers}
                    onChange={props.handleLoanOfficerChange}
                    treeCheckable={!props.officer_filter.isLoading}
                    treeData={loanOfficers}
                    allowClear
                    labelInValue={true}
                    multiple
                    treeDefaultExpandAll
                    treeNodeFilterProp='search'
                    treeNodeLabelProp='title'
                    disabled={props.officer_filter.isLoading}
                    placeholder={<Rephrase>Filter by Loan Officer</Rephrase>}
                    className='tree-filter'
                    dropdownClassName='tree-filter-dropdown'
                  />
                </Col>
              )}
              {pipeline_column_settings && pipeline_column_settings.underwriter && (
                <Col className="underwriter-col">
                  <TreeSelect
                    value={props.selectedUnderwriters}
                    onChange={props.handleUnderwriterChange}
                    treeCheckable={!props.underwriters.isLoading}
                    treeData={[{
                      title: 'Unassigned',
                      value: 'null',
                      search: 'Unassigned',
                    }, ...underwriters]}
                    allowClear
                    labelInValue={true}
                    multiple
                    treeDefaultExpandAll
                    treeNodeFilterProp='search'
                    treeNodeLabelProp='title'
                    disabled={props.underwriters.isLoading}
                    placeholder={<Rephrase>Filter by Underwriter</Rephrase>}
                    className='tree-filter'
                    dropdownClassName='tree-filter-dropdown'
                  />
                </Col>
              )}
              {
                checkPermission('display_assigned_filter_on_order_table') && (
                  <Col className="assigned-users-col">
                    <TreeSelect
                      value={props.selectedAssignedUsers}
                      onChange={props.handleAssignedUserChange}
                      treeCheckable={!props.assignedUsers.isLoading}
                      treeData={assignedUsers}
                      allowClear
                      labelInValue={true}
                      multiple
                      treeDefaultExpandAll
                      treeNodeFilterProp='search'
                      treeNodeLabelProp='title'
                      disabled={props.assignedUsers.isLoading}
                      placeholder={<Rephrase>Filter by Assigned Users</Rephrase>}
                      className='tree-filter'
                      dropdownClassName='tree-filter-dropdown'
                    />
                  </Col>
                )
              }
              {/* Appraiser Selection Filter */}
              {checkPermission('user_view_appraiser') && renderCombinedAppraiserFilter()}
              {props.show_default_filters && (
                <DefaultFilters
                  selectedDefaultViews={selectedDefaultViews}
                  handleChange={props.handleChange}
                />
              )}
            </div>
            <h6 className='ml-1 mt-4'>Custom Filters</h6>
            {availablePipelineViews.map((view) => (
              <SingleFilter
                clearable={false}
                currentCount={props?.filters?.data?.custom_pipeline_view_counts?.[view.id] || 0}
                handleChange={props.handleViewChange}
                isChecked={props.selectedPipelineViews.indexOf(view.id) > -1}
                isLoading={props.filters.isLoading}
                key={view.id}
                tag={view.id}
                tagTitle={view.name}
              />
            ))}
          </Col>
          <Col xs="12" sm="8" md="8" lg="9" xl="10">
            <Row className="">
              <Col xs="12">
                {props.getNextQueueIsEmpty &&
                  <Alert
                    message="Could not assign next order. Queue is empty."
                    type="error" showIcon closable
                    onClose={props.handleCloseGetNextQueueEmpty}
                  />
                }
              </Col>
            </Row>
            <Row className="">
              {/* Order Searchbox Filter */}
              <Col className="p-3 order-search">
                <OrderSearch { ...props } />
              </Col>

              {/* Date Range Filter */}
              {
                (props.customDateRangeDisplay === true)
                  ? (
                    <Col className='p-3'>
                      { props.orders.count } Orders:<RangePicker
                        open={true}
                        onChange={props.handleCustomDateFilterChange}
                        onOpenChange={props.onOpenChange}
                      />
                    </Col>
                  ) : (
                    <Col className='p-3'>
                      {props.orders.count} Orders:
                      <Popover
                        placement='bottomLeft'
                        content={content}
                        trigger='click'
                        onClick={props.togglePopOver}
                        visible={props.popOverState}
                        onVisibleChange={props.popOverStatus}
                      >
                        <Button
                          type='link'
                          style={{ color: '#434343', textAlign: 'left', paddingLeft: 1 }}
                        >
                          {props.dateRangeText} ▼
                        </Button>
                      </Popover>
                    </Col>
                  )
              }

              {/* Get Next Order Button */}
              {(reviewerQueues.length > 0) &&
                <Col className='p-3' style={{ textAlign: 'right' }}>
                  <OrderGetNext handleReceivedNextOrder={props.handleReceivedNextOrder} />
                </Col>
              }
            </Row>
            <Row>
              <Col className="orderListTableContainer">
                <Table
                  className="orderListTable"
                  list={
                    {
                      results: orderList,
                      filters: {
                        data: props.orders.filters,
                        orderBy: (name) => props.orders.filter({ ordering: name }),
                      },
                    }
                  }
                >
                  {cols}
                </Table>
                <Pagination
                  items={props.orders}
                  last_id
                  lastIdPerPage={props.lastIdPerPage}
                  pageName={'orders'}
                  canChangeLimit={ordersPageSize}
                  limitSelectLabel='Orders Per Page'
                  limitSelectedHandler={props.ordersLimitHandler}
                />

              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </TrackingContext.Provider>
  )
}
